/**
 * BookmarksPanel Component
 * Modern side panel for displaying and managing bookmarks
 */

"use client"

import { Button, buttonVariants } from "@/components/ui/Button"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { ArrowRight, Bookmark } from "lucide-react"
import Link from "next/link"
import { useState } from "react"
import { toast } from "sonner"
import { deleteBookmark } from "../api/bookmarks-api"
import BookmarksList from "./BookmarksList"

interface BookmarksPanelProps {
  userEmail?: string
  trigger?: React.ReactNode
  side?: "left" | "right"
}

export default function BookmarksPanel({
  userEmail,
  trigger,
  side = "right",
}: BookmarksPanelProps) {
  const [isOpen, setIsOpen] = useState(false)

  const handleDelete = async (bookmarkId: string) => {
    try {
      await deleteBookmark(bookmarkId, { userEmail })
      toast.success("Favourite removed successfully")
      // BookmarksList component will auto-refresh
    } catch (error) {
      console.error("Error deleting bookmark:", error)
      toast.error("Failed to remove favourite. Please try again.")
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Bookmark className="h-4 w-4 mr-2" />
            My Bookmark
          </Button>
        )}
      </SheetTrigger>
      <SheetContent
        side={side}
        className="w-full sm:w-[500px] sm:max-w-full overflow-y-auto flex flex-col"
      >
        <SheetHeader className="shrink-0">
          <div className="flex flex-col gap-2">
            <SheetTitle className="flex items-center gap-2">
              <Bookmark className="h-5 w-5 text-primary" />
              My Bookmark
            </SheetTitle>

              <Link href="/bookmarks" className={buttonVariants({ variant: "outline", size: "sm" })} onClick={() => setIsOpen(false)}>
                <span className="hidden sm:inline">View all</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
  
          </div>
        </SheetHeader>
        <div className="mt-6 flex-1 overflow-y-auto p-4">
          <BookmarksList
            userEmail={userEmail}
            onDelete={handleDelete}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}
